import { useState } from 'react';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { ExternalLink, Loader, Package } from 'lucide-react';
import { partnersApi } from '../../services/partnersApi';
import { ApogeePackSearchField } from '../apogee/ApogeePackSearchField';
import { EmptyState } from '../ui/EmptyState';
import { SectionHeader } from '../ui/SectionHeader';
import { ReportStatusBadge } from './PartnerPhaseBadge';
import { VersionTriad } from './VersionTriad';
import type { PartnerPack } from '../../types/partners';
import { partnerPagePath } from '../../utils/partnerLinks';
import type { PartnerNotice } from '../../utils/partnerSync';

interface PartnerPacksSectionProps {
  creatorId: string;
  packs: PartnerPack[];
  canEdit: boolean;
  /** The viewer can open Modpack Configs, for "Update the cluster". */
  canOpenConfigs: boolean;
  onChanged: () => void | Promise<void>;
  onNotice: (notice: PartnerNotice) => void;
}

/**
 * The partner's modpacks. Linking a pack to its Apogee entry lets Argus
 * watch CurseForge for it and find the clusters that run it.
 */
export const PartnerPacksSection = ({ creatorId, packs, canEdit, canOpenConfigs, onChanged, onNotice }: PartnerPacksSectionProps) => {
  const [saving, setSaving] = useState<string | null>(null);

  const link = async (pack: PartnerPack, apogeeId: number | null) => {
    if (apogeeId === pack.apogee_id) return;
    setSaving(pack.modpack_id);
    try {
      await partnersApi.setPackApogee(pack.modpack_id, apogeeId);
      onNotice({
        tone: 'success',
        text:
          apogeeId === null
            ? `Unlinked ${pack.name} from Apogee. Argus stops watching CurseForge for it.`
            : `Linked ${pack.name} to Apogee #${apogeeId}. Argus now watches CurseForge for new versions, starting from today, so files already listed are not reported as new.`,
      });
      await onChanged();
    } catch (error) {
      onNotice({ tone: 'danger', text: error instanceof Error ? error.message : 'The Apogee link could not be saved.' });
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="space-y-4">
      <SectionHeader title="Packs" icon={Package} count={packs.length} />
      {packs.length === 0 ? (
        <EmptyState
          bare
          compact
          icon={Package}
          title="No modpacks for this partner"
          description="Add a modpack to this creator from Partnerships → Modpacks, and it appears here."
        />
      ) : (
        <div className="space-y-3">
          {packs.map((pack) => (
            <div key={pack.modpack_id} className="subcard p-3 md:p-4 space-y-3">
              <div className="flex items-start gap-3">
                {pack.logo_url ? (
                  <img
                    src={pack.logo_url}
                    alt=""
                    className="w-10 h-10 rounded-md object-cover bg-dark-900/50 flex-shrink-0 ring-1 ring-glass-200/10"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                ) : (
                  <span className="icon-tile icon-tile-purple">
                    <Package className="w-4 h-4" />
                  </span>
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h4 className="text-sm font-semibold text-white truncate">{pack.name}</h4>
                    {pack.platform_url && (
                      <a href={pack.platform_url} target="_blank" rel="noopener noreferrer" className="link inline-flex items-center gap-1 text-xs">
                        <ExternalLink className="w-3 h-3" />
                        Listing
                      </a>
                    )}
                  </div>
                  {pack.latest_report ? (
                    <p className="text-xs text-glass-400 mt-1 flex items-center gap-1.5 flex-wrap">
                      Latest report:
                      <Link to={partnerPagePath(creatorId, 'updates', pack.latest_report.id)} className="link font-mono">
                        {pack.latest_report.version}
                      </Link>
                      <ReportStatusBadge status={pack.latest_report.status} />
                      <span className="text-glass-500">{formatDistanceToNow(new Date(pack.latest_report.created_at), { addSuffix: true })}</span>
                    </p>
                  ) : (
                    <p className="text-xs text-glass-500 mt-1">No update reported yet.</p>
                  )}
                </div>
              </div>

              <div className="border-t border-glass-200/10 pt-3">
                <VersionTriad pack={pack} canOpenConfigs={canOpenConfigs} />
              </div>

              <div className="max-w-xl">
                <ApogeePackSearchField
                  value={pack.apogee_id}
                  onChange={(id) => void link(pack, id)}
                  disabled={!canEdit || saving === pack.modpack_id}
                  compact
                  label="Apogee entry"
                  description={
                    pack.apogee_id
                      ? 'Argus watches CurseForge for this pack through Apogee, flags versions the partner has not reported, and finds the clusters that run it.'
                      : 'Link the pack’s Apogee entry so Argus can watch CurseForge for new versions, match them to the partner’s reports, and find the clusters that run it.'
                  }
                />
                {saving === pack.modpack_id && (
                  <p className="text-xs text-glass-400 mt-1 flex items-center gap-1.5">
                    <Loader className="w-3.5 h-3.5 animate-spin" />
                    Saving the link…
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
